interface SparklineProps {
  /** Serie temporale già ordinata (12 mesi dal più vecchio al corrente). */
  serie: { mese: string; n: number }[];
  /** Colore della linea/area — tipicamente quello della sezione del modulo. */
  color: string;
  className?: string;
}

const W = 100;
const H = 28;

/**
 * Mini grafico di andamento, disegnato in SVG puro: una sparkline non ha assi,
 * griglia né tooltip, quindi una libreria di charting sarebbe sovradimensionata
 * (e il donut di recharts resta dove serve davvero).
 *
 * Il viewBox è a coordinate fisse con `preserveAspectRatio="none"`: la curva si
 * adatta alla larghezza del contenitore senza ricalcoli in JS.
 */
export function Sparkline({ serie, color, className }: SparklineProps) {
  if (serie.length < 2) return null;

  const valori = serie.map((p) => p.n);
  const max = Math.max(...valori);
  const min = Math.min(...valori);
  // Serie tutta piatta (spesso: tutti zero): linea a metà altezza, senza area,
  // così non sembra un grafico "rotto" né suggerisce una crescita inesistente.
  const piatta = max === min;
  const span = piatta ? 1 : max - min;

  const punti = valori.map((v, i) => {
    const x = (i / (valori.length - 1)) * W;
    const y = piatta ? H / 2 : H - ((v - min) / span) * (H - 4) - 2;
    return { x, y };
  });

  const linea = punti.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
  const area = `${linea} L${W},${H} L0,${H} Z`;
  const ultimo = punti[punti.length - 1];
  const gradId = `spark-${color.replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={className}
      role="img"
      aria-label={`Andamento ultimi ${serie.length} mesi`}
    >
      {!piatta && (
        <>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.25" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gradId})`} />
        </>
      )}
      <path
        d={linea}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        opacity={piatta ? 0.35 : 1}
      />
      {!piatta && <circle cx={ultimo.x} cy={ultimo.y} r={2} fill={color} vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}
